<script setup lang="ts">
import { useDebounceFn, useWindowScroll } from '@vueuse/core'
import type { SelectMenuItem } from '@nuxt/ui'
import {
  parseDateRangeModeFromQuery,
  hasDateRangeExtra as hasDateRangeExtraMode,
  filterHotelsByDateRangeMode,
  getAdjacentSearchDatesToFetch,
} from '~/utils/dateRangeMode'

definePageMeta({
  middleware: 'auth'
})

// SEO
useSeoMeta({
  robots: 'noindex, nofollow'
})

// Config
const runtimeConfig = useRuntimeConfig()
const appConfig = useAppConfig()

// Idioma
const { locale, t } = useI18n()
const browserLang = navigator?.language ?? locale.value

// Router
const route = useRoute()
const router = useRouter()

const dateRangeMode = computed(() => parseDateRangeModeFromQuery(route.query))
const hasDateRangeExtra = computed(() => hasDateRangeExtraMode(dateRangeMode.value))

type HotelWithAvailableDate = HotelPageHotel & { _availableDate?: Date }

// Scroll
const { y } = useWindowScroll()

const scrollTopSmooth = () => {
  window?.scrollTo({ top: 0, behavior: 'smooth' })
}

const scrollTop = () => {
  window?.scrollTo({ top: 0 })
}

// Currency Store
const currencyStore = useCurrencyStore()
const { currency } = storeToRefs(currencyStore)
if (route.query?.['curr']) {
  currency.value = String(route.query?.['curr'])
}

// Search Modals Store
const searchModalsStore = useSearchModalsStore()

watch(currency, (newCurrency) => {
  router.push({
    query: {
      ...route.query,
      'curr': newCurrency,
    }
  })
  scrollTop()
})

// Ubicacion
const selectedDestStore = useDestinationStore()
const { selectedDestination, labelDestination, labelDestinationDisplay, destinationDisplayMode, destination_id, distance, latitude, longitude } = storeToRefs(selectedDestStore)

selectedDestStore.updateDestination(route)

// Al cambiar data-destinationId en la URL (p. ej. desde el menú destinos), actualizar el destino
watch(() => route.query?.['data-destinationId'], (newId, oldId) => {
  if (newId != null && String(newId) !== String(oldId)) {
    selectedDestStore.updateDestination(route)
  }
}, { immediate: false })

const lat = computed(() => latitude.value ?? 0)
const long = computed(() => longitude.value ?? 0)

const DATE_RANGE_QUERY_KEYS = ['data-dateRangeMode', 'data-dateRange', 'data-dateRangeBefore', 'data-dateRangeAfter'] as const
const DATE_RANGE_QUERY_KEYS_SET = new Set<string>(DATE_RANGE_QUERY_KEYS)

const getQueryString = computed(() => {  // query string
  const parameters = route.query
  return Object.keys(parameters).map(key => `${key}=${parameters[key]}`).join('&')
})

/** Clave del fetch principal: sin params de rango de fechas (el modo solo filtra en cliente). */
const searchFetchQueryKey = computed(() => {
  return Object.keys(route.query)
    .filter(key => !DATE_RANGE_QUERY_KEYS_SET.has(key))
    .map(key => `${key}=${route.query[key]}`)
    .join('&')
})

watch(selectedDestination, (newUbicacion) => { // scroll top si cambio de ubicacion
  if (newUbicacion) {
    scrollTop()
    // Si se obtiene una ubicación válida, cerrar el modal de selección (p. ej. tras elegir en el modal)
    const hasValue = newUbicacion && typeof newUbicacion === 'object' && Object.keys(newUbicacion).length > 0
    if (hasValue && import.meta.client) {
      searchModalsStore.closeDestinationModal()
    }
  }
})

const searchMap = async (mapObj: { center: { lat: number, lng: number }, zoom: number }) => {
  const {
    'data-destinationId': _,
    'data-search': _prevSearch,
    search: _prevSearchDup,
    ...restQuery
  } = route.query
  router.push({
    query: {
      ...restQuery,
      'data-latitude': mapObj.center.lat,
      'data-longitude': mapObj.center.lng,
      'data-distance': distance.value,
    }
  })

  const lat = mapObj.center.lat
  const lng = mapObj.center.lng
  const coordsStr = `${lat},${lng}`

  destinationDisplayMode.value = 'map_point'

  const resolved = await selectedDestStore.resolveDestinationByCoordinates(lat, lng, coordsStr)
  selectedDestination.value = resolved ?? ({
    id: null,
    label: '',
    name_translated: '',
    location: coordsStr,
  } as Destination)

  scrollTop()
}

// Fecha
const selectedDiaStore = useSelectedDayStore()
const { selectedDay, date } = storeToRefs(selectedDiaStore)

const localePath = useLocalePath()

/** Query para precargar el formulario On Demand (destino, ciudad y fecha de la búsqueda actual). */
const onDemandSearchQuery = computed<Record<string, string>>(() => {
  const out: Record<string, string> = {}
  const ds = route.query['data-search']
  if (ds) out['data-search'] = String(ds)
  else if (labelDestination.value) out['data-search'] = labelDestination.value

  const dc = route.query['data-checkinDate']
  if (dc) out['data-checkinDate'] = String(dc)
  else if (selectedDay.value) out['data-checkinDate'] = formatDateGuion(selectedDay.value)

  const destIdFromQuery = route.query['data-destinationId']
  if (destIdFromQuery != null && String(destIdFromQuery) !== '') {
    out['data-destinationId'] = String(destIdFromQuery)
  } else if (destination_id.value != null && destination_id.value !== 0) {
    out['data-destinationId'] = String(destination_id.value)
  }
  return out
})

watch(selectedDay, (newDia: Date | null, oldDia: Date | null) => {
  if (newDia && oldDia && formatDateApi(newDia) !== formatDateApi(oldDia)) {
    router.push({
      query: {
        ...route.query,
        'data-checkinDate': formatDateGuion(newDia),
        'GFakeDate': formatFakeDate(newDia)
      }
    })
    scrollTop()
  }
})

// Search
const searchQuery = computed(() => {
  const baseQuery = {
    date: date.value,
    channel_id: appConfig.channel_id,
    locale: locale.value,
    currency: currency.value,
    packhour: '3,6,24'
  }

  if (destination_id.value) {
    // Si destination_id tiene valor, se usa en la query
    return {
      destination_id: destination_id.value,
      ...baseQuery
    }
  } else {
    // Si destination_id es null, undefined o 0, se añaden lat y long
    const hasValidLocation = lat.value && long.value && lat.value !== 0 && long.value !== 0
    return {
      lat: lat.value,
      long: long.value,
      ...(hasValidLocation && { distance: distance.value }),
      ...baseQuery
    }
  }
})

// PostHog funnel
const { trackSearchCompleted, trackAlternativeNearbyDatesHotelClicked, trackAlternativeModuleViewed } = usePosthogFunnel()

function trackNearbyDatesHotelClick(item: NearbyDatesHotelEntry) {
  if (!item?.hotel?.id) return
  trackAlternativeNearbyDatesHotelClicked({
    hotel_id: Number(item.hotel.id),
    hotel_name: item.hotel?.name,
    search_date: date.value ?? '',
    available_date: formatDateGuion(item.nextAvailableDate),
    destination_id: destination_id.value ?? undefined,
    currency: currency.value,
    is_mobile: isMobile
  })
}

// Favorites Store
const favoritesStore = useFavoritesStore()
const { invalidateSearchCache } = storeToRefs(favoritesStore)

// Estado para modales de favorito
const showLoginModal = ref(false)
const showRegisterModal = ref(false)
const hotelIdForFavorite = ref<number | null>(null)

const handleToggleFavorite = async (hotelId: number) => {
  if (loggedIn.value && !userPending.value) {
    if (!favoritesStore.favoriteHotels.has(hotelId)) {
      await favoritesStore.favoriteHotel(hotelId)
    } else {
      await favoritesStore.unfavoriteHotel(hotelId)
    }
  } else if (userPending.value) {
    const toast = useToast()
    toast.add({
      title: t('labels.user_zone.menu.favoritos'),
      description: t('labels.user_zone.favoritos.completeProfile'),
      color: 'warning',
      icon: 'i-heroicons-heart',
      actions: [{
        icon: 'i-lucide-user-circle',
        label: t('labels.user_zone.menu.perfil'),
        color: 'neutral',
        variant: 'subtle',
        onClick: () => {
          navigateTo(`/${locale.value}/user-zone/profile`)
        }
      }]
    })
  } else {
    hotelIdForFavorite.value = hotelId
    showLoginModal.value = true
  }
}

// Auth Store
const authStore = useAuthStore()
const { loggedIn, user } = useUserSession()
const userPending = computed(() => {
  return (user.value as OAuthUser)?.status?.id === 2
})

function isUserTokenError(ctx: { response?: { status?: number, _data?: unknown } }): boolean {
  const data = ctx.response?._data as {
    details?: Array<{ code?: string | number }>
  } | undefined

  if (data?.details?.some(d => String(d?.code) === '0109')) {
    return true
  }

  const status = ctx.response?.status
  const text = typeof data === 'string' ? data : JSON.stringify(data ?? '')
  return (
    (status === 401 || status === 403)
    && (text.includes('0109') || /"code"\s*:\s*"0109"/.test(text))
  )
}

const { data: responseData, status, error: fetchError, refresh } = await useLazyFetch<SearchListCachedPayload>(`${useBaseUrl()}/search`, {
  key: `search-${searchFetchQueryKey.value}`,
  server: false, // No ejecutar en el servidor
  cache: 'no-store',
  async onRequest({ options }) {
    const headers = new Headers(options.headers as HeadersInit)
    const next = loggedIn.value
      ? await authStore.getAuthHeaders()
      : { 'Content-Type': 'application/json' }
    for (const [key, value] of Object.entries(next)) {
      headers.set(key, value)
    }
    options.headers = headers
  },
  query: searchQuery,
  transform(input: SearchListApiBody) {
    return {
      ...input,
      fetchechAt: new Date(),
      id: destination_id.value,
      currency: currency.value,
      date: date.value,
    }
  },
  getCachedData: (key, nuxtApp, ctx) => {
    // Si es un refresh manual, no usar la cache
    if (ctx.cause === 'refresh:manual') {
      return undefined
    }

    const cachedData = nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    if (!cachedData) {
      return // refech data
    }

    // Mirar si ha expirado la cache
    const expirationDate = new Date(cachedData?.fetchechAt)
    expirationDate.setTime(expirationDate.getTime() + 1000 * 60 * 5) // 5 minutes
    const isExpired = new Date() > expirationDate
    if (isExpired) {
      return // refech data
    }

    // Mirar si es el mismo destino
    if (destination_id.value && cachedData?.id !== destination_id.value) {
      return // refech data
    }

    // Mirar si es la misma fecha
    if (date.value && cachedData?.date !== date.value) {
      return // refech data
    }

    // Mirar si es la misma moneda
    if (currency.value && cachedData?.currency !== currency.value) {
      return // refech data
    }

    // Mirar si lat y long han cambiado
    if (lat.value && long.value && cachedData?.lat !== lat.value && cachedData?.long !== long.value) {
      return // refech data
    }

    // Mirar si se ha invalidado la cache
    if (invalidateSearchCache.value) {
      return // refech data
    }

    return cachedData
  },
  timeout: 30000,
  onResponseError: async (ctx) => {
    if (isUserTokenError(ctx)) {
      await authStore.logoutDueToInvalidUserToken()
      await refresh()
      return
    }
    logError(ctx)
  }
})
const pending = computed(() => status.value === 'pending')
const searchError = computed(() => status.value === 'error')

const serverErrorMessage = computed(() => {
  if (!fetchError.value) return null
  const data = fetchError.value?.data as ApiErrorPayload | undefined
  if (data?.details?.[0]?.message) {
    return data.details[0].message
  }
  return data?.msg || data?.error || t('errors.search')
})

// Variables para controlar el estado de carga y evitar hydration mismatch
const isInitialLoad = ref(true)
const showContent = ref(false)

const dataHoteles = ref<HotelPageHotel[]>((responseData.value as SearchListCachedPayload | null | undefined)?.data?.search?.hotels || [])
const dataSearch = ref<SearchSection>((responseData.value as SearchListCachedPayload | null | undefined)?.data?.search || {})
const dataAlternatives = ref<HotelPageHotel[]>((responseData.value as SearchListCachedPayload | null | undefined)?.data?.alternatives?.hotels || [])
const dataNear = ref<HotelPageHotel[]>((responseData.value as SearchListCachedPayload | null | undefined)?.data?.near?.hotels || [])

const dataAllAlternatives = computed(() => {
  const seen = new Set<number>()
  const unique: HotelPageHotel[] = []
  for (const h of [...dataAlternatives.value, ...dataNear.value]) {
    const id = Number(h.id)
    if (seen.has(id)) continue
    seen.add(id)
    unique.push(h)
  }
  return unique
})

// --- Disponibles en fechas cercanas (cuando < 3 resultados y sin alternativas) ---
const nearbyDatesHotels = ref<NearbyDatesHotelEntry[]>([])
const nearbyDatesPending = ref(false)

function addDays(d: Date, n: number): Date {
  const r = new Date(d)
  r.setDate(r.getDate() + n)
  return r
}

function startOfLocalDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function getSearchQueryForDate(d: Date) {
  const baseQuery = {
    date: formatDateApi(d),
    channel_id: appConfig.channel_id,
    locale: locale.value,
    currency: currency.value,
    packhour: '3,6,24'
  }
  if (destination_id.value) {
    return { destination_id: destination_id.value, ...baseQuery }
  }
  const hasValidLocation = lat.value && long.value && lat.value !== 0 && long.value !== 0
  return {
    lat: lat.value,
    long: long.value,
    ...(hasValidLocation && { distance: distance.value }),
    ...baseQuery
  }
}

function getQueryStringForDate(d: Date): string {
  const q = { ...route.query, 'data-checkinDate': formatDateGuion(d), 'GFakeDate': formatFakeDate(d) }
  return Object.keys(q).map(key => `${key}=${(q as Record<string, string>)[key]}`).join('&')
}

function isSameSearchDay(a: Date | undefined | null, b: Date | null | undefined): boolean {
  if (!a || !b) return false
  return startOfLocalDay(a).getTime() === startOfLocalDay(b).getTime()
}

function urlParamsForHotel(hotel: HotelWithAvailableDate): string {
  if (hotel._availableDate && selectedDay.value && !isSameSearchDay(hotel._availableDate, selectedDay.value)) {
    return getQueryStringForDate(hotel._availableDate)
  }
  return getQueryString.value
}

function availableDateForHotel(hotel: HotelWithAvailableDate): Date | undefined {
  if (!hotel._availableDate || !selectedDay.value) return undefined
  if (isSameSearchDay(hotel._availableDate, selectedDay.value)) return undefined
  return hotel._availableDate
}

const dateRangeMergePending = ref(false)
/** Todos los hoteles de día principal + adyacentes ya cargados; el modo activo solo filtra esta lista. */
const dateRangeMergedCache = ref<HotelWithAvailableDate[]>([])
/** YMD de días adyacentes ya pedidos a `/search` (evita repetir al ampliar el modo). */
const dateRangeAdjacentFetched = ref<Set<string>>(new Set())

const isDateRangeMergeLoading = computed(
  () => hasDateRangeExtra.value && dateRangeMergePending.value
)

const shouldShowSkeleton = computed(() => {
  return !showContent.value
    || pending.value
    || isLoading.value
    || status.value === 'pending'
    || isDateRangeMergeLoading.value
})

const isSearchResultsReady = computed(() =>
  status.value === 'success'
  && Boolean(responseData.value)
  && !pending.value
  && !isDateRangeMergeLoading.value
)

/** Hora entrada y packs: skeleton mientras recarga (evita parpadeo al cambiar día). */
const showFiltersSkeleton = computed(() => {
  if (dataHoteles.value?.length) return false
  return pending.value || status.value === 'pending' || isDateRangeMergeLoading.value
})

function revealSearchResults() {
  if (pending.value || status.value === 'pending') return
  if (status.value !== 'success') return
  if (!responseData.value) return
  if (hasDateRangeExtra.value && dateRangeMergePending.value) return
  setTimeout(() => {
    showContent.value = true
  }, 100)
}

function applyDateRangeToDisplay() {
  if (!selectedDay.value) return
  const mainHotels = ((responseData.value as SearchListCachedPayload | null | undefined)?.data?.search?.hotels ?? []) as HotelPageHotel[]

  if (!hasDateRangeExtra.value) {
    dataHoteles.value = mainHotels
    return
  }

  if (dateRangeMergedCache.value.length) {
    dataHoteles.value = filterHotelsByDateRangeMode(
      dateRangeMergedCache.value,
      dateRangeMode.value,
      selectedDay.value
    )
  } else {
    dataHoteles.value = []
  }
}

/**
 * Recalcula el rango de precios de los filtros a partir de la lista fusionada.
 * Necesario cuando el día principal no tiene hoteles (los hoteles solo vienen de
 * d±1) y `precio_max` quedaría a 0, filtrando todos los hoteles por precio.
 */
function syncPriceRangeFromMergedHotels(hotels: HotelPageHotel[]) {
  if (!hotels.length) return
  let minP = Infinity
  let maxP = 0
  for (const h of hotels) {
    const p = primerPrecio(h as Partial<Hotel>)
    if (p > 0) {
      if (p < minP) minP = p
      if (p > maxP) maxP = p
    }
  }
  if (minP === Infinity) return
  const nextMax = Math.ceil(maxP / 10) * 10
  const nextMin = Math.floor(minP / 10) * 10
  precio_max.value = nextMax
  precio_min.value = nextMin
  applyPrecioFilter(nextMax)
}

function buildDateRangeMergedList(
  mainHotels: HotelPageHotel[],
  hotelMap: Map<number, HotelWithAvailableDate>
): HotelWithAvailableDate[] {
  const ordered: HotelWithAvailableDate[] = []
  const seen = new Set<number>()
  for (const h of mainHotels) {
    const hid = Number(h.id)
    const entry = hotelMap.get(hid)
    if (entry) {
      ordered.push(entry)
      seen.add(hid)
    }
  }
  for (const entry of hotelMap.values()) {
    const hid = Number(entry.id)
    if (!seen.has(hid)) {
      ordered.push(entry)
      seen.add(hid)
    }
  }
  return ordered
}

async function fetchAndMergeDateRange(options?: { additionalDatesOnly?: Date[] }) {
  if (!hasDateRangeExtra.value || !selectedDay.value || !import.meta.client) return

  const baseDate = new Date(selectedDay.value)
  const isIncremental = (options?.additionalDatesOnly?.length ?? 0) > 0
  const datesToTry = options?.additionalDatesOnly
    ?? getAdjacentSearchDatesToFetch(baseDate, dateRangeMode.value)

  if (isIncremental && datesToTry.length === 0) {
    applyDateRangeToDisplay()
    return
  }

  dateRangeMergePending.value = true
  if (!isIncremental) showContent.value = false

  const mainHotels = ((responseData.value as SearchListCachedPayload | null | undefined)?.data?.search?.hotels ?? []) as HotelPageHotel[]

  try {
    if (datesToTry.length === 0 && !isIncremental) {
      dateRangeMergedCache.value = mainHotels.map(h => ({ ...h, _availableDate: baseDate }))
      dateRangeAdjacentFetched.value = new Set()
      applyDateRangeToDisplay()
      return
    }

    const headers = loggedIn.value ? await authStore.getAuthHeaders() : { 'Content-Type': 'application/json' }
    const hotelMap = new Map<number, HotelWithAvailableDate>()
    const mainIds = new Set<number>()

    if (isIncremental) {
      for (const h of dateRangeMergedCache.value) {
        hotelMap.set(Number(h.id), { ...h })
      }
    }

    for (const h of mainHotels) {
      const hid = Number(h.id)
      mainIds.add(hid)
      hotelMap.set(hid, { ...h, _availableDate: baseDate })
    }

    if (!isIncremental) {
      dateRangeAdjacentFetched.value = new Set()
    }
    for (const d of datesToTry) {
      dateRangeAdjacentFetched.value.add(formatDateGuion(d))
    }

    const results = await Promise.all(
      datesToTry.map(async (d) => {
        try {
          const query = getSearchQueryForDate(d)
          const res = await $fetch<SearchListApiBody>(`${useBaseUrl()}/search`, { query, headers, cache: 'no-store' })
          const hotels = res?.data?.search?.hotels ?? []
          return { date: d, hotels }
        } catch {
          return { date: d, hotels: [] as HotelPageHotel[] }
        }
      })
    )

    for (const { date: d, hotels } of results) {
      for (const h of hotels) {
        const hid = Number(h.id)
        if (!hotelMap.has(hid)) {
          hotelMap.set(hid, { ...h, _availableDate: new Date(d) })
        }
      }
    }

    const ordered = buildDateRangeMergedList(mainHotels, hotelMap)

    dateRangeMergedCache.value = ordered
    syncPriceRangeFromMergedHotels(ordered)
    applyDateRangeToDisplay()
    if (!dataHoteles.value?.length) resetFiltro()
  } finally {
    dateRangeMergePending.value = false
    revealSearchResults()
  }
}

watch(dateRangeMode, () => {
  if (!hasDateRangeExtra.value) {
    dateRangeMergedCache.value = []
    dateRangeAdjacentFetched.value = new Set()
    applyDateRangeToDisplay()
    return
  }
  if (!selectedDay.value) return

  const baseDate = new Date(selectedDay.value)
  const needed = getAdjacentSearchDatesToFetch(baseDate, dateRangeMode.value)
  const missing = needed.filter(d => !dateRangeAdjacentFetched.value.has(formatDateGuion(d)))

  if (missing.length === 0) {
    if (dateRangeMergedCache.value.length) {
      applyDateRangeToDisplay()
    } else if (responseData.value && status.value === 'success') {
      void fetchAndMergeDateRange()
    }
    return
  }

  if (responseData.value && status.value === 'success') {
    void fetchAndMergeDateRange(
      dateRangeMergedCache.value.length ? { additionalDatesOnly: missing } : undefined
    )
  }
})

async function fetchNearbyDates() {
  if (!selectedDay.value || !import.meta.client) return
  nearbyDatesPending.value = true
  nearbyDatesHotels.value = []
  const baseDate = new Date(selectedDay.value)
  const today = startOfLocalDay(new Date())
  const datesToTry = [addDays(baseDate, -2), addDays(baseDate, -1), addDays(baseDate, 1), addDays(baseDate, 2)].filter(
    d => startOfLocalDay(d).getTime() >= today.getTime()
  )
  if (datesToTry.length === 0) {
    nearbyDatesPending.value = false
    return
  }
  const headers = loggedIn.value ? await authStore.getAuthHeaders() : { 'Content-Type': 'application/json' }
  // Usar la respuesta actual de la búsqueda principal para excluir (evita race con dataHoteles y diferencias de tipo id)
  const mainHotels = (responseData.value as SearchListCachedPayload | null | undefined)?.data?.search?.hotels ?? []
  const hotelIdsInMain = new Set(mainHotels.map((h: HotelPageHotel) => Number(h.id)))
  const baseTime = baseDate.getTime()

  const results = await Promise.all(
    datesToTry.map(async (d) => {
      try {
        const query = getSearchQueryForDate(d)
        const res = await $fetch<SearchListApiBody>(`${useBaseUrl()}/search`, { query, headers, cache: 'no-store' })
        const hotels = res?.data?.search?.hotels ?? []
        return { date: d, hotels }
      } catch {
        return { date: d, hotels: [] }
      }
    })
  )

  const byHotel: Record<number, { hotel: HotelPageHotel; date: Date }> = {}
  for (const { date: d, hotels } of results) {
    for (const h of hotels) {
      if (hotelIdsInMain.has(Number(h.id))) continue
      const hid = Number(h.id)
      const existing = byHotel[hid]
      const diff = Math.abs(d.getTime() - baseTime)
      if (!existing || Math.abs(existing.date.getTime() - baseTime) > diff) {
        byHotel[hid] = { hotel: h, date: new Date(d) }
      }
    }
  }
  nearbyDatesHotels.value = Object.values(byHotel).map(({ hotel, date }) => ({ hotel, nextAvailableDate: date }))
  nearbyDatesHotels.value.sort((a, b) => a.nextAvailableDate.getTime() - b.nextAvailableDate.getTime())
  nearbyDatesPending.value = false
}

const showNearbyDatesSection = computed(() =>
  !hasDateRangeExtra.value &&
  !pending.value &&
  dataHoteles.value?.length < 3 &&
  !dataAllAlternatives.value?.length &&
  !!date.value &&
  (!!destination_id.value || (!!latitude.value && !!longitude.value && latitude.value !== 0 && longitude.value !== 0))
)

// Ejecutar fechas cercanas cuando la búsqueda principal termina (incl. dataHoteles para que esté actualizado al cambiar destino)
// y deduplicar con una clave para no lanzar dos veces (responseData y dataHoteles cambian seguidos).
const lastNearbyFetchKey = ref('')
watch([status, responseData, dataHoteles], () => {
  if (!showNearbyDatesSection.value || status.value !== 'success' || !responseData.value) {
    if (!showNearbyDatesSection.value) {
      nearbyDatesHotels.value = []
      lastNearbyFetchKey.value = ''
    }
    return
  }
  const key = `${destination_id.value ?? 'geo'}-${date.value}`
  if (key === lastNearbyFetchKey.value) return
  lastNearbyFetchKey.value = key
  void fetchNearbyDates()
}, { immediate: true })

const sortedNearbyDatesHotels = computed(() =>
  [...nearbyDatesHotels.value].sort((a, b) => a.nextAvailableDate.getTime() - b.nextAvailableDate.getTime())
)

watch(responseData, (newData) => { // Actualizar hoteles
  // No vaciar datos al iniciar una nueva búsqueda ("Buscar en esta zona"); solo actualizar cuando llegue la respuesta
  if (!newData) return

  const searchData = (newData as SearchListCachedPayload | null | undefined)?.data?.search
  const hotels = searchData?.hotels ?? []

  dateRangeMergedCache.value = []
  dateRangeAdjacentFetched.value = new Set()
  if (hasDateRangeExtra.value) {
    dataHoteles.value = []
    void fetchAndMergeDateRange()
  } else {
    dataHoteles.value = hotels
    if (!hotels.length) resetFiltro()
  }
  dataSearch.value = searchData || {}
  dataAlternatives.value = (newData as SearchListCachedPayload | null | undefined)?.data?.alternatives?.hotels || []
  dataNear.value = (newData as SearchListCachedPayload | null | undefined)?.data?.near?.hotels || []
  const maxP = Number(dataSearch.value?.summary?.max_price ?? 0)
  const minP = Number(dataSearch.value?.summary?.min_price ?? 0)
  precio_max.value = Math.ceil(maxP / 10) * 10
  applyPrecioFilter(Math.ceil(maxP / 10) * 10)
  precio_min.value = Math.floor(minP / 10) * 10
  precio_min_label.value = dataSearch.value?.summary?.min_price_label ?? ''
  currency_symbol.value = dataSearch.value?.summary?.currency_symbol ?? ''
  currencyStore.setEurExchange(dataSearch.value?.summary?.eur_exchange)
  estrellas.value = dataSearch.value?.search_stars?.map((star: SearchStarFilter) => star.abbr)
  servicios.value = dataSearch.value?.search_services?.map((service: SearchServiceFilter) => service.name)
})

// Al empezar una nueva carga (p. ej. cambio de fecha), ocultar contenido y mostrar skeleton
watch(pending, (isPending) => {
  if (isPending) {
    showContent.value = false
  } else {
    revealSearchResults()
  }
})

watch([responseData, status, dateRangeMergePending, hasDateRangeExtra], () => {
  revealSearchResults()
}, { immediate: true })

// Funnel: search_completed cuando la búsqueda termina con destino + fecha (una vez por búsqueda)
const lastSearchFunnelKey = ref<string | null>(null)
watch([status, responseData, destination_id, date, latitude, longitude], () => {
  if (status.value !== 'success' || !responseData.value || !date.value) return
  const hasDestination = !!destination_id.value || (!!latitude.value && !!longitude.value && latitude.value !== 0 && longitude.value !== 0)
  if (!hasDestination) return
  const funnelKey = `${destination_id.value ?? 'geo'}-${date.value}-${latitude.value}-${longitude.value}`
  if (lastSearchFunnelKey.value === funnelKey) return
  lastSearchFunnelKey.value = funnelKey
  const searchData = (responseData.value as SearchListCachedPayload | null | undefined)?.data?.search
  const hotels = searchData?.hotels ?? []
  trackSearchCompleted({
    destination_id: destination_id.value ?? undefined,
    destination_name: labelDestination.value || selectedDestination.value?.name_translated,
    date: date.value,
    currency: currency.value,
    locale: locale.value,
    results_count: hotels.length,
    has_destination: true
  })
}, { immediate: true })

// Funnel: alternative_module_viewed cuando se muestra cada módulo de alternativas (una vez por página)
const wasNearbyDatesModuleVisible = ref(false)
const wasAlternativesModuleVisible = ref(false)
watch([showNearbyDatesSection, nearbyDatesPending, () => sortedNearbyDatesHotels.value.length], () => {
  const isVisible = showNearbyDatesSection.value && !nearbyDatesPending.value && sortedNearbyDatesHotels.value.length > 0
  if (isVisible && !wasNearbyDatesModuleVisible.value) {
    trackAlternativeModuleViewed({
      module_type: 'nearby_dates',
      items_count: sortedNearbyDatesHotels.value.length,
      destination_id: destination_id.value ?? undefined,
      destination_name: labelDestination.value || selectedDestination.value?.name_translated,
      search_date: date.value ?? undefined,
      currency: currency.value,
      locale: locale.value,
      is_mobile: isMobile
    })
    wasNearbyDatesModuleVisible.value = true
  }
}, { immediate: true })

// Filtros

// Horas
const selectedHora = ref<SearchHoraFiltroItem[]>([])

//Precio
const precio_max = ref(Math.ceil(Number(dataSearch.value?.summary?.max_price ?? 0) / 10) * 10)
const initialPrecio = Math.ceil(Number(dataSearch.value?.summary?.max_price ?? 0) / 10) * 10
/** Valor aplicado al filtrado (actualización inmediata desde API/reset). */
const selectedPrecio = ref(initialPrecio)
/** Valor del slider; el filtro se sincroniza con debounce solo al mover el control. */
const sliderPrecio = ref(initialPrecio)
const isPriceFilterSynced = computed(() => sliderPrecio.value === selectedPrecio.value)

let skipSliderPrecioDebounce = false
let precioFilterDebounceTimer: ReturnType<typeof setTimeout> | null = null
const PRECIO_FILTER_DEBOUNCE_MS = 200

watch(sliderPrecio, (val) => {
  if (skipSliderPrecioDebounce) return
  if (precioFilterDebounceTimer) clearTimeout(precioFilterDebounceTimer)
  precioFilterDebounceTimer = setTimeout(() => {
    selectedPrecio.value = val
    precioFilterDebounceTimer = null
  }, PRECIO_FILTER_DEBOUNCE_MS)
})

function applyPrecioFilter(value: number) {
  skipSliderPrecioDebounce = true
  if (precioFilterDebounceTimer) {
    clearTimeout(precioFilterDebounceTimer)
    precioFilterDebounceTimer = null
  }
  selectedPrecio.value = value
  sliderPrecio.value = value
  skipSliderPrecioDebounce = false
}

const precio_min = ref(Math.floor(Number(dataSearch.value?.summary?.min_price ?? 0) / 10) * 10)
const precio_min_label = ref<string>(dataSearch.value?.summary?.min_price_label ?? '')
const currency_symbol = ref<string>(dataSearch.value?.summary?.currency_symbol ?? '')

// Estrellas
const estrellas = ref(dataSearch.value?.search_stars?.map((star: SearchStarFilter) => star.abbr))
const selectedEstrellas = ref([])

// Servicios
const servicios = ref(dataSearch.value?.search_services?.map((service: SearchServiceFilter) => service.name))
const selectedServicios = ref([])

// Promoción
const promo = ref(false)

// Pack (selección única; 'all' = Todos, no filtra)
const PACK_ALL = 'all' as const
const selectedPack = ref<SearchPackHours | typeof PACK_ALL>(PACK_ALL)

const packFilterItems = computed(() => [
  { label: t('buttons.todos'), value: PACK_ALL },
  { label: '3h', value: 3 as SearchPackHours },
  { label: '6h', value: 6 as SearchPackHours },
  { label: '24h', value: 24 as SearchPackHours },
])

const activePack = computed<SearchPackHours | undefined>(() =>
  selectedPack.value === PACK_ALL ? undefined : selectedPack.value
)

// Filtrar hoteles
const showHotel = (hotel: HotelPageHotel, use12HourFormat: boolean): boolean => {
  // Si el hotel tiene checkin_times vacio, no se muestra
  if (isCheckinTimesEmpty(hotel?.checkin_times ?? {})) {
    return false
  }

  // Si el hotel no tiene horas en filtroHoras, no se muestra
  const filtroHoras = selectedHora.value.length ? selectedHora.value.map((hora: SearchHoraFiltroItem) => hora.label) : []
  const arrayHorasAux = getCheckinHourKeys(hotel.checkin_times)
  const horasHotelDispo = use12HourFormat
    ? arrayHorasAux.map((hora: string) => format12Hour(hora))
    : arrayHorasAux
  if (filtroHoras.length && !filtroHoras.some((hora: string) => horasHotelDispo.includes(hora))) {
    return false
  }

  // Precio
  if (precioForPack(hotel as Partial<Hotel>, activePack.value) > selectedPrecio.value) {
    return false
  }

  // Estrellas
  if (selectedEstrellas.value?.length && !selectedEstrellas.value.some((str: string) => str === hotel?.stars?.abbr)) {
    return false
  }

  // Servicios
  const arrayServicios = Object.values(hotel?.services ?? {}).map((value: Service) => value.name)
  if (selectedServicios.value?.length && !selectedServicios.value.every((value: string) => arrayServicios.includes(value))) {
    return false
  }

  // Filtro Promo
  if (promo.value && !hotel?.promo?.length) {
    return false
  }

  // Filtro pack (selección única; "Todos" no filtra)
  if (activePack.value) {
    const hotelPack = hotel?.pack_min_price?.[activePack.value]?.price ?? 0
    if (!hotelPack) {
      return false
    }
  }

  // Si todas las condiciones de filtro se cumplen, se muestra el hotel
  return true
}

const filteredHoteles = computed(() => {
  const use12HourFormat = changeHourFormat(browserLang)
  const filterData = [...dataHoteles.value ?? []]
  return filterData.filter(hotel => showHotel(hotel, use12HourFormat))
})

const mapHotels = computed((): HotelPageHotel[] => [
  ...filteredHoteles.value,
  ...dataAlternatives.value,
  ...dataNear.value
])

// Horas disponibles
const updateHorasEntrada = () => {
  const horasCount: Record<string, number> = {}
  for (let i = 0; i < filteredHoteles.value?.length; i++) {
    const chekinTimes = filteredHoteles.value?.[i]?.checkin_times
    const arrayHoras = getCheckinHourKeys(chekinTimes)

    arrayHoras.forEach((hora: string) => {
      if (horasCount[hora]) {
        horasCount[hora]++;
      } else {
        horasCount[hora] = 1;
      }
    })
  }

  const contadorHoras = Object.keys(horasCount).map(hora => ({ hora, numHoteles: horasCount[hora] }))
  const horasEntrada = Array.from({ length: 24 }, (_, index) => {
    const hour = index
    const stringHour = `${hour.toString().padStart(2, '0')}:00`
    const hotel = contadorHoras.find(h => h.hora === stringHour)
    if (hotel?.numHoteles !== undefined) {
      return {
        label: changeHourFormat(browserLang) ? format12Hour(stringHour) : stringHour,
        numHoteles: hotel.numHoteles,
        chip: {
          color: hotel.numHoteles ? 'success' : 'error',
        }
      }
    }
  }).filter(Boolean)
  return horasEntrada
}
const horasEntrada = ref(updateHorasEntrada())

watch([selectedPack, selectedPrecio, selectedEstrellas, selectedServicios, promo], () => {
  horasEntrada.value = updateHorasEntrada()
  // Sincronizar selectedHora con nueva lista
  const labelsToKeep = selectedHora.value.map((h: SearchHoraFiltroItem) => h?.label).filter(Boolean)
  if (labelsToKeep.length === 0) return
  const selectableItems = (horasEntradaWithLabel.value ?? []).filter((item): item is SearchHoraFiltroItem =>
    Boolean(item && typeof item === 'object' && 'label' in item && (item as { type?: string }).type !== 'label'))
  selectedHora.value = selectableItems.filter((item: SearchHoraFiltroItem) => labelsToKeep.includes(item.label))
})

// Horas entrada con label de título
const horasEntradaWithLabel = computed(() => {
  if (!horasEntrada.value?.length) return []
  return [
    { type: 'label' as const, label: t('strapi.searchPage.filtroHora') },
    ...horasEntrada.value.filter((item): item is NonNullable<typeof item> => item !== undefined).map(item => ({
      label: item.label,
      numHoteles: item.numHoteles,
      chip: {
        color: (item.chip.color === 'success' ? 'success' : 'error') as 'success' | 'error'
      }
    }))
  ] as SelectMenuItem[]
})

// Reset filtro
const resetFiltro = () => {
  selectedHora.value = []
  applyPrecioFilter(precio_max.value)
  selectedEstrellas.value = []
  selectedServicios.value = []
  promo.value = false
  selectedPack.value = PACK_ALL

  isOpen.value = false
}

watch(selectedDestination, (newUbicacion) => { // reset filtro si cambio de ubicacion
  if (newUbicacion) {
    resetFiltro()
  }
})

// Check filtro
const hasFilters = computed(() => {
  return (
    (sliderPrecio.value && precio_max.value && sliderPrecio.value !== precio_max.value) ||
    selectedEstrellas.value.length > 0 ||
    selectedServicios.value.length > 0 ||
    selectedHora.value.length > 0 ||
    selectedPack.value !== PACK_ALL
  )
})

watch(
  [selectedHora, selectedPrecio, selectedEstrellas, selectedServicios, promo, selectedPack],
  () => {
    scrollTop()
  }
)

// Ordenación

// Ordenación de search
const ordenSearch: string[] = [
  t('strapi.searchPage.ordenacion.0'),
  t('strapi.searchPage.ordenacion.1'),
  t('strapi.searchPage.ordenacion.2'),
  t('strapi.searchPage.ordenacion.3'),
  t('strapi.searchPage.ordenacion.4'),
]
const selectedOrden = ref<string>(ordenSearch[0] ?? t('strapi.searchPage.ordenacion.0'))

// Solo animar reordenación cuando el usuario cambia el criterio de ordenación (no en carga inicial ni load more)
const sortJustChanged = ref(false)
watch(selectedOrden, (newVal, oldVal) => {
  if (oldVal !== undefined && newVal !== oldVal) {
    sortJustChanged.value = true
    scrollTop()
    const duration = 450
    setTimeout(() => { sortJustChanged.value = false }, duration)
  }
})

const sortData = (data: HotelPageHotel[]) => {
  const sortedData = [...data]
  const sortFunction = ordenarSearch[ordenSearch.indexOf(selectedOrden.value)]
  if (sortFunction) {
    sortedData.sort((a, b) => sortFunction(a as Partial<Hotel>, b as Partial<Hotel>))
  }
  return sortedData
}

const sortedHoteles = computed(() => {
  return sortData(filteredHoteles.value)
})

const sortAllAlternatives = computed(() => {
  return sortData(dataAllAlternatives.value)
})

watch(() => sortAllAlternatives.value.length, (length) => {
  const isVisible = length > 0
  if (isVisible && !wasAlternativesModuleVisible.value) {
    trackAlternativeModuleViewed({
      module_type: 'alternatives',
      items_count: length,
      destination_id: destination_id.value ?? undefined,
      destination_name: labelDestination.value || selectedDestination.value?.name_translated,
      search_date: date.value ?? undefined,
      currency: currency.value,
      locale: locale.value,
      is_mobile: isMobile
    })
    wasAlternativesModuleVisible.value = true
  }
}, { immediate: true })

// Slideover
const isOpen = ref(false)

// Breadcrumb
const links = computed(() => [
  {
    label: 'Home',
    to: `/${locale.value}`
  },
  {
    label: labelDestination.value
  }
])

// Data layers
const { proxy } = useScriptGoogleTagManager()

const search_country = route.query?.['data-country']
const checkin_date = route.query?.['data-checkinDate']
const user_language = arrayLangKeys[locale.value]
const fecha = new Date()
const year = fecha.getFullYear()
const month = ("0" + (fecha.getMonth() + 1)).slice(-2) // Los meses en JavaScript comienzan desde 0
const day = ("0" + fecha.getDate()).slice(-2)
const date_now = `${year}-${month}-${day}`
const time_now = `${fecha.getHours()}:${fecha.getMinutes()}:${fecha.getSeconds()}`
const phCookie = useCookie<PosthogCookie | null>(`ph_${runtimeConfig.public.POSTHOG_KEY}_posthog`)
const session_id = phCookie?.value?.distinct_id
const hotels = computed(() => {
  return filteredHoteles.value.map((hotel: HotelPageHotel) => {
    return {
      chain: hotel?.chain,
      currency: currency.value,
      hotel_id: hotel?.id,
      name: hotel?.name,
      price: precioForPack(hotel as Partial<Hotel>, activePack.value),
      star: hotel?.stars?.abbr
    }
  })
})

// packHours para GTM view_item_list (query o valor por defecto de búsqueda)
const packHours = computed(() => route.query?.['data-packHours'] ?? '3,6,24')
// destination_country_iso para Lookup Table - DL - Destination Country - ISO (item_category)
const destination_country_iso = computed(() => selectedDestination.value?.country_id ?? null)

function pushSearchDataLayer() {
  proxy.dataLayer.push({
    brainsins: {
      city: `city-${destination_id.value}`,
      pageType: "category",
    },
    checkin_date,
    city_url: `${runtimeConfig.public.BASE_URL}${selectedDestination.value?.url}`,
    date_now,
    destination_id: destination_id.value,
    destination_country_iso: destination_country_iso.value,
    hotel_avg_price: dataSearch.value?.summary?.avg_price,
    hotel_city: labelDestination.value,
    hotel_min_price: dataSearch.value?.summary?.min_price,
    hotels: hotels.value,
    number_of_purchases: 0,
    packHours: packHours.value,
    results: filteredHoteles.value.length,
    rfmc: 0,
    search: labelDestination.value,
    search_country,
    search_destination_id: destination_id.value,
    session_id,
    time_now,
    user_currency: currency.value,
    user_language
  })
}

const pushSearchDataLayerDebounced = useDebounceFn(() => {
  pushSearchDataLayer()
}, 200)

// Actualizar dataLayer cuando cambien resultados, destino o datos de búsqueda
watch(
  [filteredHoteles, dataSearch, () => destination_id.value, selectedDestination, currency],
  () => { pushSearchDataLayerDebounced() },
  { immediate: true }
)

// SEO
const canonicalUrl = computed(() => {
  const baseUrl = runtimeConfig.public.BASE_URL || ''
  return `${baseUrl}/${locale.value}/search`
})

useSeoMeta({
  title: t('meta.search.title', { count: filteredHoteles.value.length, city_name: labelDestination.value }),
  description: t('meta.search.description', { city_name: labelDestination.value }),
  ogTitle: t('meta.search.title', { count: filteredHoteles.value.length, city_name: labelDestination.value }),
  ogDescription: t('meta.search.description', { city_name: labelDestination.value }),
  ogUrl: canonicalUrl
})

useHead({
  meta: [
    { name: 'keywords', content: t('meta.search.keywords', { city_name: labelDestination.value }) }
  ],
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl
    }
  ]
})

// Hover hotel
const hoverHotelID = ref(0)

// Es mobile
const deviceStore = useDeviceStore()
const isMobile = deviceStore.isMobile

// Scroll load
const itemsPerPage = 10
const currentPage = ref(1)
const loading = ref(false)

const visibleItems = computed(() =>
  sortedHoteles.value.slice(0, currentPage.value * itemsPerPage)
)

watch(sortedHoteles, () => {
  currentPage.value = 1
})

const loadMoreItems = () => {
  if (visibleItems.value.length >= sortedHoteles.value.length) return
  if (loading.value) return
  loading.value = true
  setTimeout(() => {
    currentPage.value += 1
    loading.value = false
  }, 100) // Simula una carga de datos
}

const onScroll = () => {
  const { scrollTop, clientHeight, scrollHeight } = document.documentElement
  if (scrollTop + clientHeight >= scrollHeight - 1500) { // 500px antes de llegar al final
    loadMoreItems()
  }
}

onMounted(() => {
  isInitialLoad.value = false
  window.addEventListener('scroll', onScroll)

  // Si no hay ubicación seleccionada, abrir el modal de selección (mismo que en SearchInput)
  nextTick(() => {
    const dest = selectedDestination.value
    const hasNoDestination = !dest || (typeof dest === 'object' && Object.keys(dest).length === 0)
    if (hasNoDestination) {
      searchModalsStore.openDestinationModal()
    }
  })

  // Verificar si ya hay datos al montar el componente
  revealSearchResults()
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})

// Loading
const { isLoading } = useLoadingIndicator()

// Manejar navegación a otra ruta - llamar scrollTop antes de salir
onBeforeRouteLeave(() => {
  scrollTop()
})

const handleErrorReload = () => {
  scrollTop()
  if (import.meta.client) {
    window.location.reload()
  }
}

</script>

<template>
  <div class="w-full">
  <!-- Botón mapa -->
  <Transition
    enter-active-class="motion-safe:transition-all motion-safe:duration-300 motion-safe:ease-out motion-reduce:transition-none motion-reduce:duration-0"
    enter-from-class="opacity-0 -translate-y-4"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="motion-safe:transition-all motion-safe:duration-200 motion-safe:ease-in motion-reduce:transition-none motion-reduce:duration-0"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 -translate-y-4"
  >
    <div
      v-if="y >= 500"
      class="sm:hidden fixed top-16 inset-x-0 z-50 mt-2 flex justify-center"
    >
      <UButton icon="i-lucide-arrow-up" color="neutral" size="xs" @click="scrollTopSmooth">
        <UIcon name="i-lucide-map" class="w-5 h-5" />
      </UButton>
    </div>
  </Transition>

  <!-- Filtros -->
  <div
    class="justify-between z-10 sm:z-20 sticky top-16 backdrop-blur-lg bg-white/80 flex flex-col xl:flex-row p-5 py-3 xl:py-5 w-full sm:gap-3 max-w-screen-2xl items-start">

    <div class="w-full xl:w-fit flex items-center justify-between">

      <!-- BreadCrumb -->
      <UBreadcrumb :items="links" />

      <div class="flex">

        <!-- Filtros plegado -->
        <div class="xl:hidden flex gap-2 items-center">

          <USlideover :title="$t('buttons.filtros')" v-model:open="isOpen">

            <!-- Botón filtros -->
            <UChip inset :show="hasFilters">
              <UButton :aria-label="$t('buttons.filtros')" icon="i-lucide-sliders-horizontal" variant="subtle"
                color="neutral" @click="() => { isOpen = true }" />
            </UChip>

            <template #body>
              <div class="flex flex-col gap-4">

                <!-- Hora entrada -->
                <div v-if="dataHoteles?.length || showFiltersSkeleton">
                  <h3>{{ $t('strapi.searchPage.filtroHora') }}</h3>
                  <div class="py-2">
                    <USelectMenu clear v-if="dataHoteles?.length" :disabled="!horasEntrada?.length" size="md"
                      class="w-full" v-model="selectedHora" :items="horasEntradaWithLabel" multiple
                      :search-input="false" :placeholder="$t('strapi.searchPage.filtroHora')" :ui="{
                        item: 'gap-1 p-1 py-2'
                      }">
                      <template #leading="{ ui }">
                        <UChip v-if="selectedHora.length" color="success" inset standalone
                          :class="ui.itemLeadingChip()" />
                        <UIcon v-else name="i-lucide-clock" class="text-neutral-400 w-4 h-4" />
                      </template>
                      <template #item-label="{ item }">
                        <div v-if="item" class="flex items-center gap-1.5">
                          <span>{{ (item as SearchHoraFiltroItem).label }}</span>
                          <span class="text-muted text-xs">
                            {{ (item as SearchHoraFiltroItem).numHoteles }} {{ $t('labels.hoteles') }}
                          </span>
                        </div>
                      </template>
                      <template #empty>
                        {{ t('buttons.noDispo') }}
                      </template>
                    </USelectMenu>
                    <USkeleton v-else class="h-10 w-full rounded-full" />
                  </div>
                </div>

                <!-- Packs -->
                <div v-if="dataHoteles?.length || showFiltersSkeleton">
                  <h3>{{ $t('labels.pack') }}</h3>
                  <div v-if="dataHoteles?.length" class="py-2 w-full">
                    <URadioGroup
                      v-model="selectedPack"
                      :items="packFilterItems"
                      orientation="horizontal"
                      color="neutral"
                      size="md"
                      :ui="{ fieldset: 'gap-4', wrapper: 'ms-1' }"
                    />
                  </div>
                  <div v-else class="py-2 flex gap-4 w-full">
                    <USkeleton class="h-6 w-12 rounded-full" />
                    <USkeleton class="h-6 w-12 rounded-full" />
                    <USkeleton class="h-6 w-12 rounded-full" />
                  </div>
                </div>

                <!-- Precio -->
                <div v-if="selectedPrecio">
                  <h3>{{ $t('strapi.searchPage.filtroPrecio') }}</h3>
                  <div class="py-4">
                    <div class="flex flex-col gap-4 w-full">
                      <div class="flex gap-2 items-center">
                        <span id="precioMin-2" class="text-nowrap">{{ isRightSymbol(precio_min_label, currency_symbol) ?
                          `${precio_min} ${currency_symbol}` : `${currency_symbol} ${precio_min}` }}</span>
                        <USlider color="neutral" :step="10" size="sm" :placeholder="$t('buttons.buscar')"
                          v-model="sliderPrecio" :max="precio_max" :min="precio_min" />
                        <span id="precioMax-2" class="text-nowrap">{{ isRightSymbol(precio_min_label, currency_symbol) ?
                          `${sliderPrecio} ${currency_symbol}` : `${currency_symbol} ${sliderPrecio}` }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Estrellas -->
                <div>
                  <h3>{{ $t('strapi.searchPage.filtroEstrella') }}</h3>
                  <div class="py-2">
                    <USelectMenu id="category-2" :disabled="!estrellas?.length" size="md" class="w-full"
                      v-model="selectedEstrellas" :items="estrellas" multiple
                      :placeholder="$t('strapi.searchPage.filtroEstrella')" :search-input="false">
                      <template #item="{ item: option }">
                        <span v-if="option === '-'">{{ option }}</span>
                        <template v-else>
                          <UIcon v-for="(index) in splitEstrellas(option)?.countEstrellas" :key="index" class="text-xs"
                            name="i-heroicons-star-solid" />
                          {{ splitEstrellas(option)?.rightString }}
                        </template>
                      </template>
                    </USelectMenu>
                  </div>
                </div>

                <!-- Servicios -->
                <div class="col-span-2">
                  <h3>{{ $t('strapi.searchPage.filtroServicio') }}</h3>
                  <div class="py-2">
                    <USelectMenu id="servicios-2" :disabled="!servicios?.length" size="md" class="w-full"
                      v-model="selectedServicios" :items="servicios" multiple
                      :placeholder="$t('strapi.searchPage.filtroServicio')" :search-input="{
                        placeholder: `${t('buttons.buscar')}...`,
                      }">
                      <template #empty>
                        {{ t('buttons.noDispo') }}
                      </template>
                    </USelectMenu>
                  </div>
                </div>

                <USeparator class="my-4" />

                <div class="flex justify-between gap-2">
                  <UButton :aria-label="$t('buttons.borrar')" color="neutral" size="lg" variant="link"
                    icon="i-lucide-rotate-cw" @click="resetFiltro">{{ $t('buttons.reiniciar') }}</UButton>
                  <div>
                    <UButton :aria-label="$t('buttons.cerrar')" size="lg" color="neutral" variant="link"
                      @click="() => { isOpen = false }">{{
                        $t('buttons.cerrar') }}</UButton>
                    <UButton :aria-label="$t('buttons.aplicar')" size="lg" color="neutral" @click="() => { isOpen = false }">{{
                      $t('buttons.aplicar') }}</UButton>
                  </div>
                </div>

              </div>
            </template>

          </USlideover>

          <!-- Botón reset -->
          <UButton color="neutral" class="xl:hidden" size="md" variant="link" icon="i-lucide-rotate-cw"
            @click="resetFiltro" :aria-label="$t('buttons.reiniciar')" />

        </div>
      </div>
    </div>

    <!-- Filtros desplegado -->
    <div class="w-full xl:w-fit flex flex-wrap items-center justify-between sm:justify-end sm:gap-3">

      <!-- Hora entrada -->
      <USelectMenu clear v-if="dataHoteles?.length" :disabled="!horasEntrada?.length" size="lg"
        class="hidden xl:flex min-w-40 max-w-40 ps-9 pe-9" v-model="selectedHora" :items="horasEntradaWithLabel"
        multiple :search-input="false" :placeholder="$t('strapi.searchPage.filtroHora')" :ui="{
          item: 'gap-1 p-1 py-2'
        }">
        <template #leading="{ ui }">
          <UChip v-if="selectedHora.length" color="success" inset standalone :class="ui.itemLeadingChip()" />
          <UIcon v-else name="i-lucide-clock" class="text-neutral-400 w-4 h-4" />
        </template>

        <template #item-label="{ item }">
          <div v-if="item" class="flex items-center gap-1.5">
            <span>{{ (item as SearchHoraFiltroItem).label }}</span>
            <span class="text-muted text-xs">
              {{ (item as SearchHoraFiltroItem).numHoteles }} {{ $t('labels.hoteles') }}
            </span>
          </div>
        </template>

        <template #empty>
          {{ t('buttons.noDispo') }}
        </template>

      </USelectMenu>
      <USkeleton v-else-if="showFiltersSkeleton"
        class="hidden xl:block h-10 min-w-40 max-w-40 rounded-full shrink-0" />

      <!-- Packs -->
      <div v-if="dataHoteles?.length"
        :class="'hidden xl:flex gap-2 sm:gap-5 bg-white ring ring-inset ring-accented py-2 rounded-full pl-2.5 pr-3 justify-between w-fit'">
        <URadioGroup
          v-model="selectedPack"
          name="pack-filter-desktop"
          :items="packFilterItems"
          orientation="horizontal"
          color="neutral"
          size="md"
          :ui="{ fieldset: 'gap-3 sm:gap-4', wrapper: 'ms-1' }"
        />
      </div>
      <div v-else-if="showFiltersSkeleton"
        :class="'hidden xl:flex gap-2 sm:gap-5 bg-white ring ring-inset ring-accented py-2 rounded-full pl-2.5 pr-3 items-center w-fit'">
        <USkeleton class="h-6 w-10 rounded-full" />
        <USkeleton class="h-6 w-10 rounded-full" />
        <USkeleton class="h-6 w-10 rounded-full" />
      </div>

      <div class="hidden xl:flex flex-wrap gap-3 items-center">

        <!-- Precio -->
        <UPopover :aria-label="$t('strapi.searchPage.filtroPrecio')">
          <UButton id="price" :aria-label="$t('strapi.searchPage.filtroPrecio')" :disabled="!selectedPrecio" size="md"
            color="neutral" variant="outline" :label="$t('strapi.searchPage.filtroPrecio')"
            trailing-icon="i-lucide-chevron-down" class="w-40 justify-between" :ui="{
              base: 'py-2 px-3 hover:bg-white',
            }" />
          <template #content>
            <div class="p-4">
              <div class="flex flex-col gap-4 w-72">
                <div class="flex gap-2 items-center">
                  <span id="precioMin" class="text-nowrap">{{ isRightSymbol(precio_min_label, currency_symbol) ?
                    `${precio_min} ${currency_symbol}` : `${currency_symbol} ${precio_min}` }}</span>
                  <USlider color="neutral" :step="10" size="xs" v-model="sliderPrecio" :max="precio_max"
                    :min="precio_min" />
                  <span id="precioMax" class="text-nowrap">{{ isRightSymbol(precio_min_label, currency_symbol) ?
                    `${sliderPrecio} ${currency_symbol}` : `${currency_symbol} ${sliderPrecio}` }}</span>
                </div>
              </div>
            </div>
          </template>
        </UPopover>

        <!-- Estrellas -->
        <USelectMenu id="category" :disabled="!estrellas?.length" size="lg" class="w-40" v-model="selectedEstrellas"
          :items="estrellas" multiple :placeholder="$t('strapi.searchPage.filtroEstrella')" :search-input="false">
          <template #item-label="{ item: option }">
            <span v-if="option === '-'">{{ option }}</span>
            <template v-else>
              <UIcon v-for="(index) in splitEstrellas(option)?.countEstrellas" :key="index" class="text-xs"
                name="i-heroicons-star-solid" />
              {{ splitEstrellas(option)?.rightString }}
            </template>
          </template>
        </USelectMenu>

        <!-- Servicios -->
        <USelectMenu id="servicios" :disabled="!servicios?.length" size="lg" class="w-40" v-model="selectedServicios"
          :items="servicios" multiple :placeholder="$t('strapi.searchPage.filtroServicio')" :search-input="{
            placeholder: `${t('buttons.buscar')}...`,
          }" :ui="{
            content: 'w-56',
          }">
          <template #empty>
            {{ t('buttons.noDispo') }}
          </template>
        </USelectMenu>

      </div>

      <!-- Reset filtros -->
      <UButton color="neutral" class="hidden xl:flex" size="sm" variant="link" icon="i-lucide-rotate-cw"
        @click="resetFiltro" :aria-label="$t('buttons.reiniciar')" :label="$t('buttons.reiniciar')">
      </UButton>
    </div>
  </div>

  <div class="grid grid-cols-1 xl:grid-cols-2 max-w-screen-2xl w-full mb-16">
    <div class="z-10 bg-white xl:bg-transparent border-t border-neutral-200 xl:border-none rounded-t-3xl">
      <div class="h-full p-5">

        <!-- Bar -->
        <div class="flex justify-center pb-4 xl:hidden select-none" style="touch-action: none;">
          <span class="block h-1 w-[100px] bg-neutral-200 rounded-full"></span>
        </div>

        <!-- Usar ClientOnly para evitar hydration mismatch -->
        <ClientOnly>
          <!-- Mostrar skeleton o error inicialmente -->
          <div v-if="shouldShowSkeleton" class="h-full flex flex-col gap-y-7">
            <div v-if="searchError">
              <UEmpty icon="i-lucide-alert-circle" :description="serverErrorMessage ?? t('errors.search')"
                variant="naked" size="xl" :actions="[{
                  label: $t('buttons.reiniciar'),
                  icon: 'i-lucide-rotate-cw',
                  color: 'primary',
                  variant: 'solid',
                  onClick: handleErrorReload,
                  size: 'lg'
                }]" />
            </div>
            <template v-else>
              <div class="w-full flex justify-between mb-4">
                <USkeleton class="w-5/12 h-8 rounded-full animate-pulse" />
                <USkeleton class="w-2/12 h-8 rounded-full animate-pulse" />
              </div>
              <div class="w-full flex flex-col gap-10 sm:gap-8">
                <template v-if="!isMobile">
                  <div v-for="i in 6" :key="`sk-row-${i}`" class="flex gap-3 flex-col sm:flex-row">
                    <USkeleton class="w-full sm:w-5/12 h-44 rounded-2xl animate-pulse" />
                    <USkeleton class="w-full sm:w-7/12 h-44 rounded-2xl animate-pulse" />
                  </div>
                </template>
                <template v-else>
                  <HotelSkeleton v-for="i in 6" :key="`sk-hotel-${i}`" class="animate-pulse" />
                </template>
              </div>
            </template>
          </div>

          <!-- Contenido real con transición -->
          <Transition name="fade" mode="out-in">
            <div v-if="showContent" class="h-full flex flex-col gap-y-7">
              <div class="space-y-10">
                <!-- Sin Hoteles -->
                <UEmpty
                  v-if="isSearchResultsReady && selectedDestination && selectedDestination?.category_id === 2 && !selectedDestination?.order_ratio && !dataHoteles?.length"
                  :description="$t('strapi.searchPage.sinHoteles.titulo')" icon="i-lucide-building-2" variant="naked"
                  size="xl" :actions="[{
                    label: $t('labels.buscarOtraUbicacion'),
                    class: 'bg-byhours-gradient',
                    icon: 'i-lucide-map',
                    size: 'sm',
                    onClick: () => {
                      searchModalsStore.openDestinationModal()
                    }
                  }]" />

                <!-- Sin resultados -->
                <UEmpty v-else-if="isSearchResultsReady && !dataHoteles?.length" :description="$t('strapi.searchPage.sinResultados.titulo')"
                  variant="naked" size="xl" :actions="[{
                    label: $t('labels.buscarOtraUbicacion'),
                    class: 'bg-byhours-gradient',
                    icon: 'i-lucide-map',
                    size: 'sm',
                    onClick: () => {
                      searchModalsStore.openDestinationModal()
                    }
                  }, {
                    label: $t('buttons.buscarDia'),
                    color: 'neutral',
                    variant: 'subtle',
                    icon: 'i-lucide-calendar',
                    size: 'sm',
                    onClick: () => {
                      searchModalsStore.openCalendarModal()
                    }
                  }]" />


                <!-- Sin hoteles filtro -->
                <UEmpty v-else-if="isSearchResultsReady && isPriceFilterSynced && dataHoteles?.length && !filteredHoteles.length"
                  :description="$t('strapi.searchPage.sinResultadoFiltro.titulo')" icon="i-lucide-filter"
                  variant="naked" size="xl" :actions="[{
                    label: $t('buttons.reiniciar'),
                    icon: 'i-lucide-rotate-cw',
                    size: 'lg',
                    color: 'neutral',
                    variant: 'solid',
                    onClick: resetFiltro
                  }]" />

                <!-- Resultados hoteles -->
                <div v-else-if="isSearchResultsReady">
                  <div v-if="(responseData as SearchListCachedPayload | null)?.data?.search?.search_message" class="pb-5">
                    <UAlert icon="i-lucide-info" color="secondary" variant="subtle"
                      :title="(responseData as SearchListCachedPayload | null)?.data?.search?.search_message" />
                  </div>
                  <div class="lg:flex justify-between items-center">
                    <p class="text-lg sm:text-lg lg:text-xl py-1.5 px-2.5 lg:p-0 font-bold xl:font-normal">
                      {{ filteredHoteles.length }} {{ $t('strapi.searchPage.resultados') }} {{ labelDestinationDisplay }}
                    </p>
                    <!-- Ordenación -->
                    <div>
                      <USelectMenu v-model="selectedOrden" :items="ordenSearch" class="w-fit text-neutral-500"
                        variant="none" :ui="{ content: 'w-fit' }" :search-input="false" />
                    </div>
                  </div>

                  <TransitionGroup :name="sortJustChanged ? 'list' : 'no-anim'" tag="div" class="list-grid grid grid-cols-1 gap-10 sm:gap-8 mt-4">
                    <HotelSearchCard v-for="(hotel) in visibleItems" :key="`${destination_id}-${hotel.id}`"
                      :data="hotel" :array-horas="getHorasHotel(hotel, browserLang)"
                      :available-date="availableDateForHotel(hotel as HotelWithAvailableDate)"
                      :search-date="date ?? ''"
                      :date-range-mode="dateRangeMode"
                      :selected-pack="activePack"
                      @hover-hotel="(id: number) => hoverHotelID = id"
                      :urlParams="urlParamsForHotel(hotel as HotelWithAvailableDate)" :alternative="false"
                      @toggle-favorite="handleToggleFavorite" />
                    <div v-if="loading">
                      <div class="w-full flex flex-col gap-10 sm:gap-8">
                        <template v-if="!isMobile">
                          <div v-for="i in itemsPerPage" :key="`loading-sk-row-${i}`" class="flex gap-3 flex-col sm:flex-row">
                            <USkeleton class="w-full sm:w-5/12 h-44 rounded-2xl" />
                            <USkeleton class="w-full sm:w-7/12 h-44 rounded-2xl" />
                          </div>
                        </template>
                        <template v-else>
                          <HotelSkeleton v-for="i in itemsPerPage" :key="`loading-sk-hotel-${i}`" />
                        </template>
                      </div>
                    </div>
                  </TransitionGroup>
                </div>

                <!-- On demand -->
                <UAlert color="primary" variant="subtle" icon="i-lucide-calendar-heart">
                  <template #title>
                    <span><b>{{ $t('strapi.searchPage.onDemand.titulo') }}</b></span>
                    <UButton
                      class="px-0 md:px-2 bg-byhours-gradient bg-clip-text text-transparent"
                      :to="localePath({ path: '/on-demand', query: onDemandSearchQuery })"
                      variant="link"
                      :aria-label="$t('strapi.searchPage.onDemand.subtitulo')"
                    >{{ $t('strapi.searchPage.onDemand.subtitulo') }}</UButton>
                  </template>
                </UAlert>

                <!-- Indicador de carga suave mientras se buscan fechas cercanas -->
                <p v-if="showNearbyDatesSection && nearbyDatesPending" class="text-sm text-neutral-500 flex items-center gap-2">
                  <UIcon name="i-lucide-loader-circle" class="w-4 h-4 animate-spin" />
                  {{ $t('strapi.searchPage.buscandoFechasCercanas') }}
                </p>

                <!-- Disponibles en fechas cercanas: solo se muestra cuando ya hay resultados (no el título mientras carga) -->
                <div v-if="showNearbyDatesSection && !nearbyDatesPending && sortedNearbyDatesHotels.length > 0" class="space-y-4">
                  <p class="text-base sm:text-lg lg:text-xl font-medium">
                    {{ $t('strapi.searchPage.disponiblesFechasCercanas') }} {{ labelDestinationDisplay }}
                  </p>
                  <div class="grid grid-cols-1 gap-10 sm:gap-12">
                    <div v-for="item in sortedNearbyDatesHotels" :key="`nearby-${destination_id}-${item.hotel.id}`"
                      @click="trackNearbyDatesHotelClick(item)">
                      <HotelSearchCard :data="item.hotel" :array-horas="getHorasHotel(item.hotel as Partial<Hotel>, browserLang)"
                        :available-date="item.nextAvailableDate"
                        :selected-pack="activePack"
                        @hover-hotel="(id: number) => hoverHotelID = id"
                        :urlParams="getQueryStringForDate(item.nextAvailableDate)" :alternative="false"
                        @toggle-favorite="handleToggleFavorite" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Resultados alternatives -->
              <div v-if="sortAllAlternatives.length">
                <div class="flex justify-between items-center">
                  <p class="text-base sm:text-lg lg:text-xl">
                    {{ $t('strapi.searchPage.resultadosAlternativos') }} {{ labelDestinationDisplay }}
                  </p>
                </div>
                <div ref="cardContainer" class="grid grid-cols-1 gap-10 sm:gap-8 mt-4">
                  <HotelSearchCard :data="hotel" :array-horas="getHorasHotel(hotel, browserLang)"
                    v-for="(hotel) in sortAllAlternatives" :key="`alt-${destination_id}-${hotel.id}`"
                    :selected-pack="activePack"
                    @hover-hotel="(id: number) => hoverHotelID = id" :urlParams="getQueryString" :alternative="false"
                    @toggle-favorite="handleToggleFavorite" />
                </div>
              </div>
            </div>
          </Transition>

          <!-- Fallback para el servidor -->
          <template #fallback>
            <div class="h-full flex flex-col gap-y-7">
              <div class="w-full flex justify-between mb-4">
                <USkeleton class="w-5/12 h-8 rounded-full animate-pulse" />
                <USkeleton class="w-2/12 h-8 rounded-full animate-pulse" />
              </div>
              <div class="w-full flex flex-col gap-10 sm:gap-8">
                <template v-if="!isMobile">
                  <div v-for="i in 6" :key="`sk-row-${i}`" class="flex gap-3 flex-col sm:flex-row">
                    <USkeleton class="w-full sm:w-5/12 h-44 rounded-2xl animate-pulse" />
                    <USkeleton class="w-full sm:w-7/12 h-44 rounded-2xl animate-pulse" />
                  </div>
                </template>
                <template v-else>
                  <HotelSkeleton v-for="i in 6" :key="`sk-hotel-${i}`" class="animate-pulse" />
                </template>
              </div>
            </div>
          </template>
        </ClientOnly>
      </div>
    </div>

    <!-- Mapa -->
    <div class="z-0 h-[65vh] xl:h-[calc(100vh-9rem)] order-first xl:order-last sticky top-30 sm:top-48 xl:top-36">
      <div class="h-full px-5 pb-5">
        <div class="h-full bg-neutral-50 flex justify-center items-center rounded-3xl relative">
          <ClientOnly>
            <HotelMap :pending="pending" @search-map="searchMap" :boundHoteles="filteredHoteles"
              :url-params="getQueryString" :lat="Number(lat)" :lon="Number(long)" :hover-hotel="hoverHotelID"
              :all-hotels="mapHotels" :selected-pack="activePack" @toggle-favorite="handleToggleFavorite" />
            <template #fallback>
              <USkeleton class="w-full h-full rounded-3xl" />
            </template>
          </ClientOnly>
        </div>
      </div>
    </div>


    <!-- Modales de autenticación (Mapa) -->
    <AuthModals v-model:show-login-modal="showLoginModal" v-model:show-register-modal="showRegisterModal"
      :hotel-id="hotelIdForFavorite ?? 0" @login-success="() => {
        if (hotelIdForFavorite) favoritesStore.favoriteHotel(hotelIdForFavorite)
        invalidateSearchCache = true
        reloadNuxtApp()
      }" @register-success="() => {
        if (hotelIdForFavorite) favoritesStore.favoriteHotel(hotelIdForFavorite)
      }" />
  </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Animación de reordenación solo al cambiar el criterio de ordenación (no en carga ni load more) */
.list-grid {
  position: relative;
}
.list-move {
  transition: transform 0.45s cubic-bezier(0.4, 0, 0.2, 1);
}
/* Sin animación para no-anim: primera carga y load more solo usan el fade del contenedor */
.no-anim-move,
.no-anim-enter-active,
.no-anim-leave-active {
  transition: none;
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: .5;
  }
}

/* Transición suave cuando aparece el contenido real */
.h-full>div:not([v-if]) {
  transition: opacity 0.15s ease-in-out;
}
</style>